import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styled from 'styled-components';

import { Button } from '../../../../components/Toolkit/Button/Button';

import { useUserContext } from '../../../../contexts/UserContext';

import { convertMilesToKm } from '../../../../helpers/formatting';

import { navigateTo } from '../../../../helpers/navigation';
import { PAGE } from '../../../../helpers/pages';
import { ValuationToolFields } from '../../../../components/CarValuationPage/components/ValuationTool/ValuationToolFields';
import {
  valuationSchema,
  type ValuationFormValues,
} from '../../../../components/CarValuationPage/components/ValuationTool/ValuationTool.schema';
import { media } from '../../../../helpers/breakpoints';
import getConfig from 'next/config';
import type { Image } from '../../../../api/types/hubpageApiTypes';
import { MILEAGE_UNIT } from '../../../../types';

export interface IValuationLookupComponent {
  ctaText: string;
  valuationLookupDestinationPage: string;
  hero_cta: string;
  baseUrl: string;
  heroSponsoredText?: string;
  heroSponsoredImage?: Image;
}

const ValuationLookupContainer = styled.div`
  position: relative;
`;

const ValuationLookupWrapper = styled.div<{ pageName?: string }>`
  /* margin: 104px auto 0; */
  margin: ${({ pageName }) =>
    pageName === PAGE.INSTANT_OFFER_VALUATION
      ? '96px auto 0;'
      : '104px auto 0;'};
  ${media.large} {
    margin: 192px auto 0;
  }
`;
const STextWrapper = styled.div<{ sponsored?: boolean }>`
  margin: ${({ theme }) => `${theme.spacing.XL64} auto ${theme.spacing.M24}`};
  display: flex;
  justify-content: center;
  flex-direction: ${({ sponsored }) => sponsored && 'column'};

  ${media.large} {
    margin-bottom: ${({ theme }) => theme.spacing.M32};
  }
`;

const Text = styled.h1<{ pageName?: string }>`
  ${({ theme }) => theme.fontSize.H24}
  color: ${({ theme }) => theme.colors.WHITE};
  display: flex;
  align-self: flex-end;
  margin: 0 auto;

  max-width: 290px;
  text-align: center;

  ${media.medium} {
    max-width: 592px;
  }

  ${media.large} {
    ${({ theme }) => theme.fontSize.H32}
  }
`;

const FormWrapper = styled.div`
  margin: 0 auto;
  width: 100%;

  ${media.large} {
    width: 760px;
  }
`;

const FormContainer = styled.div`
  margin: ${({ theme }) => `0 ${theme.spacing.S8}`};
  ${media.large} {
    margin: 0;
  }
`;

const ValuationContainer = styled.div`
  display: flex;
  flex-direction: column;

  background-color: ${({ theme }) => theme.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme }) => `${theme.spacing.M24} ${theme.spacing.M16}`};
  max-width: 100%;

  ${media.large} {
    padding: ${({ theme }) => theme.spacing.M24};
  }
`;

const ValuationContent = styled.div`
  margin: 0 auto;
  width: 100%;

  ${media.large} {
    width: 592px;
  }
`;

type dimension = {
  width: number;
  height: number;
};

const SponsoredText = styled.div<{
  mobileDimension: dimension;
  desktopDimension: dimension;
}>`
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;

  margin: ${({ theme }) => theme.spacing.M16} auto;

  ${media.large} {
    margin-bottom: 0;
  }

  p {
    ${({ theme }) => theme.fontSize.M14}
    color: white;
    font-weight: ${({ theme }) => theme.fontWeight.regular};
  }

  img {
    width: ${({ mobileDimension }) => mobileDimension.width}px;
    height: ${({ mobileDimension }) => mobileDimension.height}px;

    ${media.large} {
      width: ${({ desktopDimension }) => desktopDimension.width}px;
      height: ${({ desktopDimension }) => desktopDimension.height}px;
    }
  }
`;

function ValuationLookupComponent({
  ctaText,
  valuationLookupDestinationPage,
  hero_cta,
  baseUrl,
  heroSponsoredText,
  heroSponsoredImage,
}: IValuationLookupComponent) {
  const {
    publicRuntimeConfig: { CDN_STATIC_ASSETS },
  } = getConfig();
  const { width, height } = heroSponsoredImage?.dimensions ?? {
    width: 0,
    height: 0,
  };

  const { width: mobileWidth, height: mobileHeight } = heroSponsoredImage
    ?.mobile?.dimensions ?? { width: 0, height: 0 };

  const sponsoredHero = Boolean(heroSponsoredText && heroSponsoredImage);

  const { userProfile } = useUserContext();
  const { handleLogin } = useUserContext();
  const pageName =
    valuationLookupDestinationPage === 'Instant Offer Valuation'
      ? PAGE.INSTANT_OFFER_VALUATION
      : PAGE.PRIVATE_VALUATION;

  const methods = useForm<ValuationFormValues>({
    resolver: zodResolver(valuationSchema),
    defaultValues: {
      registration: '',
      mileage: '',
      mileageType: MILEAGE_UNIT.KILOMETRES,
    },
  });

  function onsubmit(values: ValuationFormValues) {
    const mileage =
      values.mileageType === MILEAGE_UNIT.MILES
        ? convertMilesToKm(Number(values.mileage))
        : values.mileage;

    const destinationUrl = `${baseUrl}/${pageName}/${values.registration}/${mileage}`;

    if (!userProfile) {
      handleLogin(destinationUrl);
    }

    if (userProfile) {
      navigateTo(destinationUrl);
    }
  }

  return (
    <ValuationLookupContainer>
      <ValuationLookupWrapper pageName={pageName}>
        <STextWrapper sponsored={sponsoredHero}>
          <Text>{ctaText}</Text>
          {sponsoredHero && (
            <SponsoredText
              mobileDimension={{
                height: mobileHeight ? mobileHeight : 0,
                width: mobileWidth ? mobileWidth : 0,
              }}
              desktopDimension={{
                height: height ? height : 0,
                width: width ? width : 0,
              }}
            >
              {heroSponsoredText && <p>{heroSponsoredText} </p>}
              {heroSponsoredImage && (
                <img src={heroSponsoredImage?.url} alt="" role="presentation" />
              )}
            </SponsoredText>
          )}
        </STextWrapper>

        <FormWrapper>
          <FormContainer>
            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit(onsubmit)}
                noValidate
                id="valuationLookupForm"
              >
                <ValuationContainer>
                  <ValuationContent>
                    <ValuationToolFields cdnUrl={CDN_STATIC_ASSETS} />

                    <Button type="submit" data-testid="login">
                      {hero_cta}
                    </Button>
                  </ValuationContent>
                </ValuationContainer>
              </form>
            </FormProvider>
          </FormContainer>
        </FormWrapper>
      </ValuationLookupWrapper>
    </ValuationLookupContainer>
  );
}

export { ValuationLookupComponent };
